/* eslint-disable max-lines, max-depth, max-depth, max-statements, complexity */
// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapActions, mapState } from "vuex";
import { markRaw } from "vue";
import alert from "../../../../../share/alert";
import loading from "../../../../../share/components/vue/ProcessingIcon";
import WrappedTeleport from "../../../../../share/components/vue/WrappedTeleport";
import config from "../../../../../share/config";
import util from "../../../../../share/util/utils";
import dataMonitorExecute from "../../../../vue/individualWorkSupport/dataMonitor/DataMonitorMeasure.vue";
import logWrapper from "../../../../../share/logWrapper";
import {
  WORKER_TASK, WORKER_EVENT, setSystem,
  getDid, splitSystemAndItemId, getTranslateValue
} from "../../../dataMonitor/dataMonitorUtil";
import { fixNumberWithDecimal } from "../../../../../common/nodejs/mathFunctions";
import apiService from '../../../../../share/util/api/service';
import { WS146_OTHER_CALCULATION_DID } from './dmDataPointConversion';

import WorkSupport146Worker from "./WorkSupport146Worker";
import individualDiagnose from "../../../../../share/util/individualDiagnose";

const ERR_MSG_KEY = {
  INFO_GET: "get",
  COMMUNICATION: "communication",
};

// ポーリング間隔（ミリ秒）
const INTERVAL = {
  WAIT_TIME: 1000, // データモニタ開始のPollingのCompletedまでPollingの待ち時間
  WORKER_SLEEP: 500, // Workerの待機時間
};

export default {
  name: "dataMonitorWs146",
  data() {
    return {
      processing: false,
      stopProcessing: false,
      checkProcessing: false,
      conversionList: {},
      // 変換情報を早く引くため、DIDごとのインデックスを持つオブジェクト
      didConversionMap: markRaw({}),
      // 選択項目
      selectedFuncs: [],
      selectedFunctionHash: {},
      // グラフ設定
      graphConfigInfo: {
        xRange: 0,
        yRange: {},
        lineSetting: {}
      },
      completeResolve: () => { },

      // プロジェクトID
      projectId: "",
      // 計測UUID
      measuredId: "",

      // Polling中 判定 FLG
      isPolling: true,
      // ポーリング停止指示
      hasStopOrder: false,
      // 計測開始時間(ナノ秒単位のUNIXタイムスタンプ)。ただしミリ秒以下は0になるように調整
      baseTime: null,
      // 計測開始時間のミリ秒以下が0となるように、調整した時間
      baseTimeOffset: null,
      // エラーフラグ
      isError: false,
      // サインアウト
      signout: false,

      // Workerエラーフラグ
      workerError: false,
      // マーカー情報
      markerInfo: markRaw([]),
      // 項目ごとのデータ（単位、平均、最大最小など）
      funcValues: {},
      // 疑似Workerインスタンス
      workSupport146Worker: null,
      // データ再生からの呼び出しか否か
      isDataRecollection: false,
      // systemId
      systemId: "",
      // 子コンポーネントのMeasureを明示的に消す
      unmountMeasure: false,
      // HEV系AT対応：テストケースメニュータイトルキー
      hevAtMenuTitleKey: "",
    };
  },

  props: {
    // 項目選択画面表示フラグ
    showSelect: { type: Boolean, default: true, required: true },
    // 全画面モーダルの表示有無
    showModal: { type: Boolean, default: false, required: false },
    // 計測データ再生中フラグ
    inPlaying: { type: Boolean, default: false, required: true },
    // 親画面名称
    parentName: { type: String, default: "data_monitor", required: false },
    // DM機能の設定インスタンス
    dataMonitorFunctionSettings: { type: Object, required: true },
    // 計測画面全体をteleportさせるモーダルダイアログのID
    modalContentId: { type: String, default: undefined, required: false },
    // グラフ設定ボタンなどをteleportさせる行先のDOMのID
    graphSettingsDestinationId: { type: String, default: undefined, required: false },
  },

  components: {
    "processing-view": loading,
    "data-monitor-execute": dataMonitorExecute,
    "wrapped-teleport": WrappedTeleport
  },

  watch: {
    baseTime: {
      handler(val) {
        this.$emit('base-time-changed', val);
      },
    },
  },

  computed: {
    ...mapState({
      language: (state) => state.systems.language,
      netAppId: (state) => state.netAppConnector.pairingAppId,
      userProperty: (state) => state.userProperty,
      individualMenus: (state) => state.vehicle.individualMenus,
      projectDetails: (state) => state.cache.projectDetails,
      cachedProjectDataSource: state => state.cache.projectDataSource,
    }),
    isDisplayProcessing() {
      return this.processing || this.stopProcessing;
    },
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),
    ...mapActions("observation", ["usageCountUp"]),

    /**
     * componentのmounted時に呼び出す処理
     */
    mountedLogic() {
    },

    /**
     * 子コンポーネントのDataMonitorMeasureのMount・Unmountを親コンポーネントに通知する
     * @param {boolean} isMounted Mountされたか否か
     */
    measureMountedEvent(isMounted) {
      this.$emit("measure-mounted", isMounted);
    },

    /** 計測結果を破棄 */
    clearMeasuredData() {
      this.dataMonitorList = [];
      this.baseTime = null;
      this.baseTimeOffset = null;
      this.funcValues = {};
    },

    /** 計測データ再生/停止状態切替 */
    togglePlaying(flg) {
      this.$emit("toggle-playing", flg);
    },

    /** 選択項目の言語変更データ受取 */
    changeSelectedFuncs(list) {
      const selectedFuncsHash = {};
      for (const selectedFunction of list) {
        const { data_monitor_id, ...conv } = selectedFunction;
        selectedFuncsHash[data_monitor_id] = conv;
      }
      this.selectedFuncs = list;
      this.selectedFunctionHash = selectedFuncsHash;
    },

    /** グラフ設定情報を保持 */
    setGraphConfig(xRange, yRange, lineSetting) {
      this.graphConfigInfo.xRange = xRange;
      // yRangeに項目が存在していれば更新、存在しなければ追加、設定済みの項目はそのまま
      Object.keys(yRange).forEach((did) => {
        this.graphConfigInfo.yRange[did] = yRange[did];
      });

      // lineSettingに項目が存在していれば更新、存在しなければ追加、設定済みの項目はそのまま
      if (lineSetting) {
        Object.keys(lineSetting).forEach((did) => {
          this.graphConfigInfo.lineSetting[did] = lineSetting[did];
        });
      }
    },

    /** グラフ項目数を設定 */
    changeCheckValue(checkLines) {
      this.$emit("change-check-value", checkLines);
    },

    /** 計測画面の範囲を取得 */
    getAreaStyle() {
      const className = this.showModal ? "dm-area-modal" : "dm-area-rc";
      return {
        [className]: true,
      };
    },

    /**
     * 子コンポーネントから計測データを取得する
     * @returns 計測データ
     */
    getMeasureData() {
      return this.$refs.data_monitor_execute.convertedData;
    },

    /**
     * タイムラインコンポーネントからマーカー情報を取得する
     * @returns マーカー情報
     */
    getMarkers() {
      return this.$refs.data_monitor_execute.getMarkers();
    },

    /**
     * 変換情報を取得して保持する
     * @param {string} requirementId 対象のrequirementId
     * @returns {boolean} 処理の成否
     */
    async setConversionList(requirementId) {
      try {
        this.processing = true;
        const monitorConversionList = {};
        this.didSizes = {};
        // monitorConversion API
        const apiResult = await apiService.getParamCall(
          apiService.WSP,
          `/systems/${this.systemId}/monitorConversion`,
          { requirement_id: requirementId, region_info: this.cachedProjectDataSource }
        );

        // APIの結果に、conversion_listがない場合エラーとする
        const conversionList = apiResult?.data?.conversion_list.flat();
  
        if (conversionList) {
          const findFFDi18nKey = individualDiagnose.getFFDi18nKeyWithRegion(
            this, null, this.systemId
          );
          // 配列でID+翻訳語のタイトル+並び順をDID毎に持たせる
          this.selectedFuncs = conversionList.map((convData, index) => {
            return {
              data_monitor_id: setSystem(this.systemId, convData.did),
              menu_title: WS146_OTHER_CALCULATION_DID.includes(getDid(convData.did)) ? util.trimStr(this.$t(`wsDid${this.systemId}.${convData.did}`)) : util.trimStr(this.$t(`${findFFDi18nKey}.${convData.did}`)),
              display_order: index
            };
          });
          // システム名をdidに付与して、結果を戻り値の変数に詰める
          conversionList.forEach(convData => monitorConversionList[setSystem(this.systemId, convData.did)] = convData.info);

          const didSizesWithSystem = Object.keys(apiResult.data.did_sizes).reduce((accumulator, currentKey) => {
            return { ...accumulator, [setSystem(this.systemId, currentKey)]: apiResult.data.did_sizes[currentKey] };
          }, {});
          this.didSizes = Object.assign(this.didSizes, didSizesWithSystem);
        } else {
          throw new Error('getMonitorConversion() getParamCall() error: ', { cause: config.INDIVIDUAL_API_CALL_ERROR_GET_INVALID_RESPONSE });
        }

        this.didConversionMap = {};
        // 変換処理で変換情報を早く引くためのオブジェクトを別途作成。1つのDIDに複数の信号線があるため、DIDをキーに配列で保持
        Object.keys(monitorConversionList).forEach((itemId, index) => {
          const did = getDid(itemId);
          if (this.didConversionMap[did]) {
            this.didConversionMap[did].push(index);
          } else {
            this.didConversionMap[did] = [index];
          }
        });
      
        // monitorConversion APIのレスポンスサイズが1未満の場合はmonitorConversion API失敗とする
        if (Object.keys(monitorConversionList).length < 1) {
          return false;
        }
        this.conversionList = monitorConversionList;
        return true;
        
      } catch (e) {
        logWrapper.log(e.messeage, e.cause || '');
        this.showCommonError(ERR_MSG_KEY.COMMUNICATION);
        return false;
      } finally {
        this.processing = false;
      }
    },

    /**
     * データ再生を開始する
     * @param {string} measuredId 計測ID
     * @param {string} s3path s3path
     * @param {number} totalNumberOfFiles グラフデータの総ファイル数
     * @param {number} startTime グラフデータの計測開始時間
     * @param {string} specifiedSystemId 再生するECUのID
     * @param {string} projectId 再生するプロジェクトのID
     * @param {string} requirementId 再生する計測データのrequirementId
     */
    async startPlayApi(measuredId, s3path, totalNumberOfFiles, startTime, specifiedSystemId, projectId, requirementId) {
      // WS.146のデータ再生情報取得処理を開始する
      this.systemId = specifiedSystemId;
      this.isDataRecollection = true;
      if (this.measuredId === measuredId) {
        // 言語変更時にデータの再取得を実施しない
        return;
      }
      this.projectId = projectId;
      this.measuredId = measuredId;

      // 再生FLGを開始に変更
      this.togglePlaying(true);

      this.selectedFuncs = [];
      this.processing = true;
      this.isPolling = true;

      try {
        // DID情報取得
        const isSucceedConversionList = await this.setConversionList(requirementId);
        // monitorConversion API失敗の場合は以降の処理は行わない。
        if (!isSucceedConversionList) {
          // ボタンの活性・非活性変更
          this.togglePlaying(false);
          return;
        }

        this.hasStopOrder = false;
        // 再生データ取得中はローディングを消す
        this.processing = false;

        this.startWorkSupport146Polling(projectId, measuredId, s3path, totalNumberOfFiles, startTime);
        // 再生データの取得の終了待ち
        while (this.isPolling && !this.hasStopOrder) {
          await util.sleep(INTERVAL.WAIT_TIME);
        }

      } finally {
        // 処理中フラグOFF
        this.processing = false;
        this.togglePlaying(false);
      }
    },

    /**
     * WorkSupport146用のAPIによるポーリングの開始処理
     * @param {string} projectId プロジェクトID
     * @param {string} measuredId 計測ID
     * @param {string} s3path s3path
     * @param {number} totalNumberOfFiles グラフデータの総ファイル数
     * @param {number} startTime グラフデータの計測開始時間
     */
    async startWorkSupport146Polling(projectId, measuredId, s3path, totalNumberOfFiles, startTime) {
      const conversionKeys = Object.keys(this.conversionList);
      const conversionValues = Object.values(this.conversionList);
      const startMessage = {
        type: WORKER_TASK.START,
        project_id: projectId,
        measurement_id: measuredId,
        s3path: s3path,
        totalNumberOfFiles: totalNumberOfFiles,
        startTime: startTime,
        conversionKeys: JSON.parse(JSON.stringify(conversionKeys)),
        conversionValues: JSON.parse(JSON.stringify(conversionValues)),
        didConversionMap: JSON.parse(JSON.stringify(this.didConversionMap)),
        didSizes: JSON.parse(JSON.stringify(this.didSizes)),
        systemId: this.systemId
      };
      this.startWorker(startMessage, this.workSupport146WorkerListener);
    },

    /**
     * Web workerの開始処理
     * @param {string} workerType 開始するWebWorkerの種別
     * @param {object} startMessage Workerに開始を通知するメッセージ
     * @param {function} listenerCallback Workerからのメッセージを受け取る関数
     */
    async startWorker(startMessage, listenerCallback) {
      // Workerを作成
      // HACK: 従来データモニタではWorkerを利用して別インスタンスで大きなデータを扱っていたが、
      // intdashではなくAPIを使用して取得しようとすると、従来のapi/service.jsでは
      // 親スレッドで初期化した情報を持ち越せない等の問題があるため、Workerを利用せずに呼び出し、疑似的に動かす
      this.workSupport146Worker = new WorkSupport146Worker(listenerCallback);

      this.workSupport146Worker.start(
        startMessage.project_id,
        startMessage.measurement_id,
        startMessage.s3path,
        startMessage.totalNumberOfFiles,
        startMessage.startTime,
        startMessage.conversionKeys,
        startMessage.conversionValues,
        startMessage.didConversionMap,
        startMessage.didSizes,
        startMessage.systemId,
        this.cachedProjectDataSource
      );

      // Workerエラーフラグのリセット
      this.workerError = false;

      // 停止ボタン等で計測停止が指示されるまで待機する
      while (!this.hasStopOrder) {
        // Workerでエラーがあった場合は待機を終了
        if (this.workerError) {
          break;
        }

        // 一定時間スリープ
        await util.sleep(INTERVAL.WORKER_SLEEP);
      }
      // ポーリング停止
      this.isPolling = false;
    },


    /**
     * 計測データ取得処理の停止
     */
    async stopFetchingMeasurement() {
      this.hasStopOrder = true;
      this.isPolling = false;
      // スリープ分待ち、Workerの停止と直前の実施していた処理を終了させる
      await util.sleep(INTERVAL.WAIT_TIME);
      // 再表示のためにクリアする
      this.measuredId = '';
      this.clearMeasuredData();
    },

    /**
     * funcValuesの表示値を翻訳する
     * @param {object} funcValues 計測データの最大値・最小値・平均値を持つオブジェクト
     * @returns {object} 表示用の値を翻訳したfuncValues
     */
    translateFuncValues(funcValues) {
      const result = {};

      for (const did of Object.keys(funcValues)) {
        const funcVal = funcValues[did];
        const conversionInfo = this.conversionList[did];

        // decExStrの場合、minDispとmaxDispを翻訳する
        if (conversionInfo.data_type === 'decExStr') {
          const [systemName, itemId] = splitSystemAndItemId(did);

          let translatedMax = getTranslateValue(
            this, itemId, funcVal.maxDisp, conversionInfo, null, systemName
          );
          translatedMax = translatedMax || fixNumberWithDecimal(funcVal.max, conversionInfo.digit_after_decimal);
          let translatedMin = getTranslateValue(
            this, itemId, funcVal.minDisp, conversionInfo, null, systemName
          );
          translatedMin = translatedMin || fixNumberWithDecimal(funcVal.min, conversionInfo.digit_after_decimal);

          result[did] = {
            ...funcVal, maxDisp: translatedMax, minDisp: translatedMin
          };
        } else {
          // それ以外はそのまま保持
          result[did] = funcVal;
        }
      }
      return result;
    },

    /**
     * WorkSupport146Workerからのメッセージ受信処理
     * @param {object} event Workerから通知されるイベント
     */
    workSupport146WorkerListener(event) {
      const data = event.data;

      // エラー受信の場合
      if (data.type === WORKER_EVENT.START_ERROR || data.type === WORKER_EVENT.RUNTIME_ERROR) {
        this.isPolling = false;
        this.processing = false;
        // Workerでエラーあり
        this.workerError = true;

        // エラーダイアログ表示
        this.showCommonError(ERR_MSG_KEY.COMMUNICATION);
      }
      else if (data.type === WORKER_EVENT.MEASUREMENT_INFO) {
        // 基準時刻を設定
        this.baseTime = data.measurement.basetime;
        this.baseTimeOffset = data.measurement.offset;

        // マーカー設定。タグのsharedがtrueであるか、userIdが自分の場合は表示可能なマーカー
        this.markerInfo = data.measurement.markers.filter(marker => marker.tag && (
          marker.tag.shared.toLowerCase() === 'true' || marker.tag.userId === this.userProperty.userId
        ));
        // 総データポイント数
        this.receivedDataPoints = data.measurement.received_data_points;
      }
      else if (data.type === WORKER_EVENT.CHUNK_DATA) {// グラフデータ
        if (data.chunkData.length === 0 || !this.inPlaying) {
          this.hasStopOrder = true;
        }
        // 変換＆画面描画
        this.dataMonitorList = JSON.parse(JSON.stringify(data.chunkData));
        this.funcValues = this.translateFuncValues(data.funcValues);
        this.$emit("update-total-count", data.originTotalCount);
      }
      else if (data.type === WORKER_EVENT.SUCCESS_END) {
        this.hasStopOrder = true;
        // ワーカーの処理正常終了時、全データ取得完了として総データポイント数を通知する
        this.$emit("update-total-count", this.receivedDataPoints);
      }
      // ワーカー停止の場合
      else if (data.type === WORKER_EVENT.STOPPED) {
        // Workerの停止処理が完了したら、Worker自体を終了する
        // HACK: Workerは作成していないので、インスタンスをnullにしておく
        this.hasStopOrder = true;
        this.workSupport146Worker = null;
      }
    },

    /**
     * その他のエラー
     * @param {string} action エラーメッセージの種別
     */
    showCommonError(action) {
      let messageKey;
      switch (action) {
        case ERR_MSG_KEY.INFO_GET:
          messageKey = "individual.error_data_monitor_info_get";
          break;
        // 一般的な通信エラー
        case ERR_MSG_KEY.COMMUNICATION:
        default:
          messageKey = "individual.error_communication_error";
          break;
      }

      if (!this.isError) {
        this.isError = true;
        alert.showError(this, null, this.$t(messageKey), () => { this.editFlg(); });
      }
    },

    /**
     * エラーフラグをOFFにする
     */
    editFlg() {
      this.isError = false;
    },

    // グラフ設定
    handleConfigureGraph() {
      this.$refs.data_monitor_execute.handleConfigureGraph();
    },

    /** データモニタの停止 */
    execStopMonitor() {
      this.$emit('exec-stop-monitor');
    },

    /** 項目選択に戻る */
    backSelect() {
      this.$emit('back-select');

      this.unmountMeasure = true;
      // 1度描画してMeasureコンポーネントを確実に消す
      this.$nextTick(() => {
        this.unmountMeasure = false;
      });
    },
  },

  created() {
    // noop
  },

  mounted() {
    this.mountedLogic();
  },

  beforeMount() {
    // 計測データを初期化。変更が多いためdataに持たせたくなく、warningとなるためbeforeMountで初期化する
    this.dataMonitorList = [];
  },
  
  beforeUnmount() {
    // Worker停止に失敗していた場合、画面を離れる前にWorkerを終了する
    if (this.workSupport146Worker) {
      this.workSupport146Worker.stop();
    }
  }
};
